import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, Utensils, Activity, FileText, GraduationCap, Smartphone, Calendar, RefreshCw, ArrowRight, ArrowLeft, ChevronDown, X, FileQuestion } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'PRÉ-CONSULTA',
    icon: FileQuestion,
    description: 'O atendimento começa antes mesmo de você me encontrar. No questionário pré-consulta, você vai me contar mais sobre sua rotina, alimentação, histórico de saúde e objetivos. Com essas informações, consigo entender melhor o seu contexto e tornar a consulta mais direcionada e eficiente para você.',
  },
  {
    number: '02',
    title: 'ANAMNESE',
    icon: ClipboardList,
    description: 'Neste primeiro momento, quero entender sua rotina, hábitos alimentares, histórico de saúde, preferências e objetivos. Essa etapa é essencial para que eu possa construir um plano alimentar personalizado, alinhado à sua realidade e pensado para gerar resultados de forma saudável e sustentável.',
  },
  {
    number: '03',
    title: 'ANÁLISE ALIMENTAR',
    icon: Utensils,
    description: 'Aqui vou entender, na prática, como é sua alimentação no dia a dia, considerando horários, escolhas e hábitos alimentares. Essas informações me permitem adaptar o plano alimentar à sua rotina, tornando-o mais simples, realista e fácil de manter no longo prazo.',
  },
  {
    number: '04',
    title: 'ANTROPOMETRIA',
    icon: Activity,
    description: 'Nesta etapa, avalio com técnica e cuidado medidas como peso, altura e circunferências para entender melhor sua composição corporal. Esses dados permitem acompanhar sua evolução ao longo do tempo e ajustar o plano alimentar de forma mais precisa, sempre respeitando seu corpo e seus objetivos.',
  },
  {
    number: '05',
    title: 'PLANEJAMENTO DO PLANO ALIMENTAR',
    icon: FileText,
    description: 'Com base nas informações coletadas durante a anamnese, análise alimentar e avaliação antropométrica, elaboro um plano alimentar personalizado, considerando suas necessidades nutricionais, rotina, preferências e objetivos.',
  },
  {
    number: '06',
    title: 'ORIENTAÇÃO E EDUCAÇÃO NUTRICIONAL',
    icon: GraduationCap,
    description: 'Nesta etapa, explico como aplicar o plano alimentar na prática. Conversamos sobre escolhas alimentares, possíveis substituições, organização das refeições e estratégias para lidar com situações comuns do cotidiano.',
  },
  {
    number: '07',
    title: 'APLICATIVO',
    icon: Smartphone,
    description: 'Cada paciente recebe acesso a um aplicativo personalizado, onde já encontra seu plano alimentar completo e pronto para seguir. Pelo app, você acompanha suas refeições, consulta orientações e mantém contato direto comigo, facilitando o dia a dia e tornando o acompanhamento nutricional mais prático e acessível.',
  },
  {
    number: '08',
    title: 'ACOMPANHAMENTO',
    icon: Calendar,
    description: 'O processo não termina com a entrega do plano alimentar. Durante o acompanhamento, observo sua adaptação às estratégias propostas, dificuldades que possam surgir e possíveis ajustes necessários.',
  },
  {
    number: '09',
    title: 'REAVALIAÇÃO E SUPORTE',
    icon: RefreshCw,
    description: 'Realizamos reavaliações para analisar sua evolução, mudanças na composição corporal e adaptação ao plano alimentar. Ofereço suporte online para esclarecer dúvidas e ajudar você a manter constância.',
  }
];

const StepPreview = ({ step, index, delay }) => {
  const Icon = step.icon;
  return (
    <motion.div
      className="flex flex-col items-center gap-2 relative"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      data-testid={`step-preview-${index}`}
    >
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-brand-green flex items-center justify-center shadow-lg relative">
        <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" strokeWidth={1.5} />
        <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow border-2 border-brand-green">
          <span className="text-[10px] font-bold text-brand-green">{step.number}</span>
        </div>
      </div>
      <span className="text-[11px] md:text-xs font-semibold text-[#1A1A1A] text-center max-w-[80px] md:max-w-[100px] leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {step.title}
      </span>
    </motion.div>
  );
};

const ExpandedSteps = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const step = steps[currentStep];
  const Icon = step.icon;

  const goNext = () => setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  const goPrev = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  return (
    <div className="relative bg-[#F2F0E9] py-16 md:py-24" data-testid="steps-expanded-section">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2311830f' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '80px 80px'
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-6 md:right-12 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors z-20"
          data-testid="steps-close-button"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Progress bar */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentStep ? 'bg-brand-green w-8' : idx < currentStep ? 'bg-brand-green/40 w-3' : 'bg-gray-300 w-3'
              }`}
              data-testid={`step-dot-${idx}`}
            />
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.35 }}
            className="flex flex-col items-center text-center"
          >
            <motion.div
              className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-brand-green flex items-center justify-center shadow-2xl relative mb-8"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Icon className="w-14 h-14 md:w-18 md:h-18 text-white" strokeWidth={1.5} />
              <div className="absolute -top-2 -right-2 w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-brand-green">
                <span className="text-lg md:text-xl font-bold text-brand-green" style={{ fontFamily: 'Playfair Display, serif' }}>{step.number}</span>
              </div>
            </motion.div>

            <h3 className="text-2xl md:text-4xl font-bold text-[#1A1A1A] mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {step.title}
            </h3>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl">
              {step.description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        <div className="flex items-center justify-center gap-6 mt-12">
          <button
            onClick={goPrev}
            disabled={currentStep === 0}
            className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-brand-green hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed text-[#1A1A1A]"
            data-testid="steps-prev-button"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          <span className="text-sm font-semibold text-gray-500">
            {currentStep + 1} / {steps.length}
          </span>

          <button
            onClick={goNext}
            disabled={currentStep === steps.length - 1}
            className="w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-brand-green hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed text-[#1A1A1A]"
            data-testid="steps-next-button"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>

        {/* CTA on last step */}
        {currentStep === steps.length - 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center mt-10"
          >
            <a
              href="https://wa.me/5535988366028?text=Ol%C3%A1%2C%20vim%20pelo%20site%20e%20gostaria%20de%20agendar%20uma%20consulta."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-brand-green text-white px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:bg-[#0d6a0c] transition-all inline-flex items-center gap-3"
              data-testid="steps-cta-button"
            >
              Agendar Consulta
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const HorizontalSteps = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
    setTimeout(() => {
      const el = document.getElementById('processo-expanded');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setTimeout(() => {
      const el = document.getElementById('processo');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  return (
    <div id="processo">
      {!isExpanded && (
        <div className="relative bg-[#F2F0E9] py-20 md:py-28 overflow-hidden" data-testid="steps-preview-section">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2311830f' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '80px 80px'
            }}
          />

          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
            <motion.div
              className="text-center mb-12 md:mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Como funciona a
                <span className="text-brand-green"> Consulta</span>
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
                Um processo completo em 9 etapas, pensado para gerar resultados reais e duradouros.
              </p>
            </motion.div>

            <div className="flex items-center justify-center gap-3 md:gap-5 flex-wrap mb-12 md:mb-16">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <StepPreview step={step} index={index} delay={index * 0.08} />
                  {index < steps.length - 1 && (
                    <motion.div
                      className="hidden sm:flex items-center"
                      initial={{ opacity: 0, scaleX: 0 }}
                      whileInView={{ opacity: 1, scaleX: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.08 + 0.1 }}
                    >
                      <div className="w-6 md:w-10 h-[2px] bg-brand-green opacity-30 rounded-full" />
                      <ArrowRight className="w-3 h-3 md:w-4 md:h-4 text-brand-green opacity-40 -ml-1" />
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <button
                onClick={handleExpand}
                className="group bg-brand-green text-white px-8 md:px-10 py-4 md:py-5 rounded-full font-semibold text-base md:text-lg shadow-xl hover:shadow-2xl hover:bg-[#0d6a0c] transition-all inline-flex items-center gap-3 animate-pulse-slow"
                data-testid="saiba-mais-button"
              >
                Saiba mais
                <ChevronDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
              </button>
            </motion.div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div id="processo-expanded">
          <ExpandedSteps onClose={handleClose} />
        </div>
      )}
    </div>
  );
};

export default HorizontalSteps;
